import React, { useState, useRef, useEffect } from "react";
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Container,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Radio,
	RadioGroup,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import LoginTwoToneIcon from "@mui/icons-material/LoginTwoTone";
import LogoutTwoToneIcon from "@mui/icons-material/LogoutTwoTone";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import CameraAltTwoToneIcon from "@mui/icons-material/CameraAltTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { useApiMutation } from "src/hooks/useApiMutation";
import type {
	ITicketScanPayload,
	ITicketScanResponse,
} from "../type/ticketScanType";
import QuickQRScannerModal from "./QuickQRScannerModal";

interface ScanFormValues {
	ticket_no: string;
	scan_type: "IN" | "OUT";
}

interface ILastScanSuccess {
	ticket_no: string;
	scan_type: string;
	visitor_no?: string;
	visitor_name?: string;
	visitor_email?: string;
	visitor_phone?: string;
	quantity?: number;
	time: string;
}

const ScanTicketPage: React.FC = () => {
	const theme = useTheme();
	const inputRef = useRef<HTMLInputElement>(null);

	const [quickScanOpen, setQuickScanOpen] = useState<boolean>(false);

	const [lastScanSuccess, setLastScanSuccess] = useState<ILastScanSuccess | null>(null);

	const {
		register,
		control,
		handleSubmit,
		setValue,
		watch,
		resetField,
		formState: { errors },
	} = useForm<ScanFormValues>({
		defaultValues: {
			ticket_no: "",
			scan_type: "IN",
		},
	});

	const watchScanType = watch("scan_type");

	const { mutate: processScan, isPending: isScanning } = useApiMutation<
		ITicketScanPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/admin/visitor/ticket/scan",
		invalidateQueries: ["TicketScans", "AllVisitorTickets"],
	});

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	const onScanSubmit = (data: ScanFormValues) => {
		const ticketNumber = data.ticket_no.trim();
		if (!ticketNumber) return;

		const payload: ITicketScanPayload = {
			ticket_no: ticketNumber,
			scan_type: data.scan_type || "IN",
		};

		processScan(payload, {
			onSuccess: (res: any) => {
				const scanData: ITicketScanResponse = res?.data || res || {};
				setLastScanSuccess({
					ticket_no: scanData?.ticket_no || ticketNumber,
					scan_type: (scanData?.scan_type || data.scan_type || "IN") as string,
					visitor_no: scanData?.visitor_no,
					visitor_name: scanData?.visitor_name,
					visitor_email: scanData?.visitor_email,
					visitor_phone: scanData?.visitor_phone,
					quantity: scanData?.quantity,
					time: new Date().toLocaleTimeString(),
				});
				resetField("ticket_no");
				setTimeout(() => {
					if (inputRef.current) {
						inputRef.current.focus();
					}
				}, 100);
			},
		});
	};

	const handleCameraScan = (decodedText: string) => {
		const ticketNumber = decodedText.trim();
		if (!ticketNumber) return;

		setQuickScanOpen(false);
		setValue("ticket_no", ticketNumber);

		const currentScanType = watchScanType || "IN";
		const payload: ITicketScanPayload = {
			ticket_no: ticketNumber,
			scan_type: currentScanType,
		};

		processScan(payload, {
			onSuccess: (res: any) => {
				const scanData: ITicketScanResponse = res?.data || res || {};
				setLastScanSuccess({
					ticket_no: scanData?.ticket_no || ticketNumber,
					scan_type: (scanData?.scan_type || currentScanType || "IN") as string,
					visitor_no: scanData?.visitor_no,
					visitor_name: scanData?.visitor_name,
					visitor_email: scanData?.visitor_email,
					visitor_phone: scanData?.visitor_phone,
					quantity: scanData?.quantity,
					time: new Date().toLocaleTimeString(),
				});
				resetField("ticket_no");
				setTimeout(() => {
					if (inputRef.current) {
						inputRef.current.focus();
					}
				}, 100);
			},
		});
	};

	return (
		<>
			<Helmet>
				<title>BTTF - Scan Ticket</title>
			</Helmet>

			<Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
				{/* Page Header */}
				<Box
					sx={{
						mb: 3,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 2,
					}}
				>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
							<QrCodeScannerTwoToneIcon
								sx={{ fontSize: 32, color: "primary.main" }}
							/>
							<Typography variant="h3" fontWeight={700}>
								Scan Ticket
							</Typography>
						</Box>
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
						<Button
							variant="contained"
							color="primary"
							startIcon={<CameraAltTwoToneIcon />}
							onClick={() => setQuickScanOpen(true)}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 700,
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
							}}
						>
							Quick Camera Scan
						</Button>

						<Button
							component={RouterLink}
							to="/visitor/ticket-list"
							variant="outlined"
							color="primary"
							startIcon={<ConfirmationNumberTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Ticket List
						</Button>
					</Box>
				</Box>

				{/* Scanner Card */}
				<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2 }}>
					<CardContent sx={{ p: { xs: 2, sm: 3 } }}>
						<Box sx={{ maxWidth: 480, mx: "auto" }}>
							{lastScanSuccess && (
								<Paper
									elevation={0}
									sx={{
										mb: 2.5,
										p: 2,
										borderRadius: 2,
										border: "1px solid",
										borderColor: alpha(theme.palette.success.main, 0.35),
										bgcolor: alpha(theme.palette.success.main, 0.05),
									}}
								>
									{/* Top Banner */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1.5,
											pb: 1,
											borderBottom: "1px dashed",
											borderColor: alpha(theme.palette.success.main, 0.25),
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<CheckCircleTwoToneIcon color="success" sx={{ fontSize: 22 }} />
											<Typography variant="subtitle1" fontWeight={700} color="success.dark">
												Scan Successful
											</Typography>
											<Chip
												label={
													lastScanSuccess.scan_type === "IN"
														? "IN (Entrance)"
														: "OUT (Exit)"
												}
												color={
													lastScanSuccess.scan_type === "IN"
														? "success"
														: "info"
												}
												size="small"
												sx={{
													fontWeight: 700,
													borderRadius: 1,
													height: 22,
													fontSize: "0.75rem",
												}}
											/>
										</Box>
										<IconButton
											size="small"
											onClick={() => setLastScanSuccess(null)}
											sx={{ color: "text.secondary", p: 0.5 }}
										>
											<CloseIcon sx={{ fontSize: 18 }} />
										</IconButton>
									</Box>

									{/* Visitor & Ticket Details Grid */}
									<Grid container spacing={1.2}>
										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary" display="block">
												Ticket No
											</Typography>
											<Typography variant="body2" fontWeight={700} color="primary.main">
												{lastScanSuccess.ticket_no}
											</Typography>
										</Grid>

										{lastScanSuccess.visitor_no && (
											<Grid item xs={12} sm={6}>
												<Typography variant="caption" color="text.secondary" display="block">
													Visitor ID
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													{lastScanSuccess.visitor_no}
												</Typography>
											</Grid>
										)}

										{lastScanSuccess.visitor_name && (
											<Grid item xs={12} sm={6}>
												<Typography variant="caption" color="text.secondary" display="block">
													Visitor Name
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													{lastScanSuccess.visitor_name}
												</Typography>
											</Grid>
										)}

										{lastScanSuccess.quantity !== undefined && (
											<Grid item xs={12} sm={6}>
												<Typography variant="caption" color="text.secondary" display="block">
													Quantity / Persons
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													{lastScanSuccess.quantity} Person(s)
												</Typography>
											</Grid>
										)}

										{lastScanSuccess.visitor_phone && (
											<Grid item xs={12} sm={6}>
												<Typography variant="caption" color="text.secondary" display="block">
													Phone
												</Typography>
												<Typography variant="body2" fontWeight={500}>
													{lastScanSuccess.visitor_phone}
												</Typography>
											</Grid>
										)}

										{lastScanSuccess.visitor_email && (
											<Grid item xs={12} sm={6}>
												<Typography variant="caption" color="text.secondary" display="block">
													Email
												</Typography>
												<Typography
													variant="body2"
													fontWeight={500}
													sx={{ wordBreak: "break-all" }}
												>
													{lastScanSuccess.visitor_email}
												</Typography>
											</Grid>
										)}

										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary" display="block">
												Scan Time
											</Typography>
											<Typography variant="body2" fontWeight={500} color="text.secondary">
												{lastScanSuccess.time}
											</Typography>
										</Grid>
									</Grid>
								</Paper>
							)}

							<Box
								component="form"
								onSubmit={(e) => {
									e.preventDefault();
									handleSubmit(onScanSubmit)();
								}}
							>
								{/* Scan Type Radio Cards */}
								<Box sx={{ mb: 2.2 }}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										textTransform="uppercase"
										letterSpacing={0.5}
										sx={{ display: "block", mb: 1, fontSize: "0.75rem" }}
									>
										Select Gate Scan Type
									</Typography>
									<Controller
										name="scan_type"
										control={control}
										render={({ field }) => (
											<FormControl component="fieldset" fullWidth>
												<RadioGroup
													row
													{...field}
													sx={{
														display: "grid",
														gridTemplateColumns: "1fr 1fr",
														gap: 1,
													}}
												>
													<Paper
														variant="outlined"
														sx={{
															px: 1.5,
															py: 0.75,
															borderRadius: 1.5,
															borderColor:
																field.value === "IN"
																	? "primary.main"
																	: "divider",
															bgcolor:
																field.value === "IN"
																	? alpha(
																		theme.palette.primary.main,
																		0.08
																	)
																	: "transparent",
															cursor: "pointer",
															transition: "all 0.2s",
														}}
														onClick={() => setValue("scan_type", "IN")}
													>
														<FormControlLabel
															value="IN"
															control={<Radio color="primary" size="small" sx={{ p: 0.5 }} />}
															sx={{ m: 0 }}
															label={
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.75,
																		ml: 0.5,
																	}}
																>
																	<LoginTwoToneIcon color="primary" fontSize="small" />
																	<Box>
																		<Typography
																			variant="body2"
																			fontWeight={600}
																			color={
																				field.value === "IN"
																					? "primary.main"
																					: "text.primary"
																			}
																			sx={{ fontSize: "0.85rem" }}
																		>
																			IN (Entrance)
																		</Typography>
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.1 }}
																		>
																			Visitor entry
																		</Typography>
																	</Box>
																</Box>
															}
														/>
													</Paper>

													<Paper
														variant="outlined"
														sx={{
															px: 1.5,
															py: 0.75,
															borderRadius: 1.5,
															borderColor:
																field.value === "OUT"
																	? "primary.main"
																	: "divider",
															bgcolor:
																field.value === "OUT"
																	? alpha(
																		theme.palette.primary.main,
																		0.08
																	)
																	: "transparent",
															cursor: "pointer",
															transition: "all 0.2s",
														}}
														onClick={() => setValue("scan_type", "OUT")}
													>
														<FormControlLabel
															value="OUT"
															control={<Radio color="primary" size="small" sx={{ p: 0.5 }} />}
															sx={{ m: 0 }}
															label={
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.75,
																		ml: 0.5,
																	}}
																>
																	<LogoutTwoToneIcon color="primary" fontSize="small" />
																	<Box>
																		<Typography
																			variant="body2"
																			fontWeight={600}
																			color={
																				field.value === "OUT"
																					? "primary.main"
																					: "text.primary"
																			}
																			sx={{ fontSize: "0.85rem" }}
																		>
																			OUT (Exit)
																		</Typography>
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.1 }}
																		>
																			Visitor exit
																		</Typography>
																	</Box>
																</Box>
															}
														/>
													</Paper>
												</RadioGroup>
											</FormControl>
										)}
									/>
								</Box>

								{/* Ticket Number Input */}
								<Box sx={{ mb: 2.5 }}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										textTransform="uppercase"
										letterSpacing={0.5}
										sx={{ display: "block", mb: 0.75, fontSize: "0.75rem" }}
									>
										Ticket Number
									</Typography>
									<TextField
										fullWidth
										size="small"
										placeholder="e.g. TCK261003"
										inputRef={inputRef}
										{...register("ticket_no", {
											required: "Please enter or scan a ticket number",
										})}
										error={Boolean(errors.ticket_no)}
										helperText={errors.ticket_no?.message}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<ConfirmationNumberTwoToneIcon color="primary" fontSize="small" />
												</InputAdornment>
											),
										}}
										sx={{
											"& input": {
												fontSize: "0.92rem",
												fontWeight: 600,
												letterSpacing: 0.5,
											},
										}}
									/>
								</Box>

								<LoadingButton
									type="submit"
									fullWidth
									variant="contained"
									color="primary"
									size="medium"
									loading={isScanning}
									startIcon={<QrCodeScannerTwoToneIcon fontSize="small" />}
									sx={{
										py: 1,
										fontSize: "0.9rem",
										fontWeight: 700,
										borderRadius: 1.5,
										boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
									}}
								>
									<span>Confirm & Scan Ticket</span>
								</LoadingButton>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</Container>

			{/* Quick QR Camera Scanner Modal */}
			{quickScanOpen && (
				<QuickQRScannerModal
					open={quickScanOpen}
					onClose={() => setQuickScanOpen(false)}
					onScan={handleCameraScan}
					scanType={watchScanType}
					onScanTypeChange={(type) => setValue("scan_type", type)}
				/>
			)}
		</>
	);
};

export default ScanTicketPage;
